import { useCallback } from 'react';

interface UseAudioReturn {
  playSound: (elementId: string, volume?: number) => void;
  setVolume: (elementId: string, volume: number) => void;
  pause: (elementId: string) => void;
  play: (elementId: string) => Promise<void>;
  toggle: (elementId: string) => boolean;
}

/**
 * Hook for managing audio playback
 * Provides utilities for playing, pausing, and controlling volume of audio elements
 */
export const useAudio = (): UseAudioReturn => {
  const getAudioElement = useCallback((elementId: string): HTMLAudioElement | null => {
    return document.querySelector(`#${elementId}`) as HTMLAudioElement | null;
  }, []);

  const playSound = useCallback((elementId: string, volume = 0.4) => {
    const audio = getAudioElement(elementId);
    if (audio) {
      audio.volume = volume;
      audio.play().catch(() => {});
    }
  }, [getAudioElement]);

  const setVolume = useCallback((elementId: string, volume: number) => {
    const audio = getAudioElement(elementId);
    if (audio) {
      audio.volume = Math.max(0, Math.min(1, volume));
    }
  }, [getAudioElement]);

  const pause = useCallback((elementId: string) => {
    const audio = getAudioElement(elementId);
    if (audio) {
      audio.pause();
    }
  }, [getAudioElement]);

  const play = useCallback(async (elementId: string): Promise<void> => {
    const audio = getAudioElement(elementId);
    if (audio) {
      await audio.play().catch(() => {});
    }
  }, [getAudioElement]);

  const toggle = useCallback((elementId: string): boolean => {
    const audio = getAudioElement(elementId);
    if (audio) {
      if (audio.paused) {
        audio.play().catch(() => {});
        return true;
      } else {
        audio.pause();
        return false;
      }
    }
    return false;
  }, [getAudioElement]);

  return { playSound, setVolume, pause, play, toggle };
};

export default useAudio;
